import { SettingsFieldRow, SettingsSection } from 'components/Settings';
import { useAuth } from 'contexts/auth';
import useChangePasswordForm from 'hooks/settings/useChangePasswordForm';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Form,
    IconButton,
    PasswordInput,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { getPasswordRequirements } from 'utils/password';
import { CHANGE_PASSWORD_FIELD } from 'utils/settings';
import { SettingsPageContext } from '../../PageProvider';

const ChangePassword = ({ sectionId }) => {
    const { auth } = useAuth();
    const { setSectionDirty } = useContext(SettingsPageContext);
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const { form, errors, save, saveField, updateField } = useChangePasswordForm({
        onDirtyChange: (value) => setSectionDirty(sectionId, value),
        onSaved: () => setDialogOpen(false),
    });

    const requirements = getPasswordRequirements(form.new);
    const requirementLabels = [
        ['length', 'account-settings.password.req-one-min-characters', 'At least 8 characters'],
        ['uppercase', 'account-settings.password.req-two-uppercase', 'At least 1 uppercase character'],
        ['lowercase', 'account-settings.password.req-three-lowercase', 'At least 1 lowercase character'],
        ['number', 'account-settings.password.req-four-number', 'At least 1 number'],
        ['special', 'account-settings.password.req-five-symbol', 'At least 1 symbol'],
    ];

    return (
        <SettingsSection id={sectionId} sectionGap={4} title={t('account-settings.password.title', 'Change Password')}>
            {auth?.is_third_party ? (
                <Text color='light'>
                    {t(
                        'account-settings.password.google-note',
                        "You can't change your password here because you signed up using your Google account. Please manage your password through your Google account settings."
                    )}
                </Text>
            ) : (
                <>
                    <SettingsFieldRow
                        sx={{
                            '& > *:last-child': {
                                maxWidth: { mobile: 'none', desktop: 400 },
                            },
                        }}
                        label={
                            <Text sx={{ my: 'auto' }} variant='p2' weight='medium'>
                                {t('account-settings.password.setup-title', 'Set Up A New Password')}
                            </Text>
                        }
                    >
                        <Button
                            sx={{
                                ml: { mobile: 0, desktop: 'auto' },
                                width: { mobile: '100%', desktop: 'auto' },
                            }}
                            onClick={() => setDialogOpen(true)}
                        >
                            {t('controls.change', 'Change')}
                        </Button>
                    </SettingsFieldRow>
                    <Text color='light'>
                        {t(
                            'account-settings.password.setup-description',
                            'Make sure your new password is strong, incorporating numbers, and symbols.'
                        )}
                    </Text>
                    <ResponsiveDialog open={dialogOpen} scroll='paper' onClose={() => setDialogOpen(false)}>
                        <DialogTitle>
                            <Stack alignItems='center' direction='row' justifyContent='space-between'>
                                <Text component='h2' variant='h6' weight='medium'>
                                    {t('account-settings.password.title', 'Change Password')}
                                </Text>
                                <IconButton
                                    aria-label={t('controls.close', 'Close')}
                                    kind='exit'
                                    size='medium'
                                    variant='square'
                                    onClick={() => setDialogOpen(false)}
                                />
                            </Stack>
                        </DialogTitle>
                        <Divider />
                        <DialogContent>
                            <Form
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    save();
                                }}
                            >
                                <Stack spacing={4} sx={{ minWidth: { mobile: 'auto', tablet: 420, desktop: 480 } }}>
                                    <PasswordInput
                                        error={Boolean(errors.current)}
                                        helperText={errors.current}
                                        label={t('account-settings.password.current-password', 'Current Password')}
                                        value={form.current}
                                        onChange={(event) =>
                                            updateField(CHANGE_PASSWORD_FIELD.CURRENT, event.target.value)
                                        }
                                        onBlur={() => saveField(CHANGE_PASSWORD_FIELD.CURRENT)}
                                    />
                                    <PasswordInput
                                        error={Boolean(errors.new)}
                                        helperText={errors.new}
                                        label={t('account-settings.password.new-password', 'New Password')}
                                        value={form.new}
                                        onChange={(event) => updateField(CHANGE_PASSWORD_FIELD.NEW, event.target.value)}
                                        onBlur={() => saveField(CHANGE_PASSWORD_FIELD.NEW)}
                                    />
                                    <Stack component='ul' spacing={1} sx={{ m: 0 }}>
                                        {requirementLabels.map(([key, translationKey, fallback]) => (
                                            <Requirement key={key} $satisfied={requirements[key]} component='li'>
                                                {t(translationKey, fallback)}
                                            </Requirement>
                                        ))}
                                    </Stack>
                                    <PasswordInput
                                        error={Boolean(errors.confirmNew)}
                                        helperText={errors.confirmNew}
                                        label={t(
                                            'account-settings.password.confirm-new-password',
                                            'Confirm New Password'
                                        )}
                                        value={form.confirmNew}
                                        onChange={(event) =>
                                            updateField(CHANGE_PASSWORD_FIELD.CONFIRM_NEW, event.target.value)
                                        }
                                        onBlur={() => saveField(CHANGE_PASSWORD_FIELD.CONFIRM_NEW)}
                                    />
                                </Stack>
                            </Form>
                        </DialogContent>
                        <Divider />
                        <DialogActions>
                            <Stack direction='row' justifyContent='flex-end' spacing={3} sx={{ width: '100%' }}>
                                <Button variant='tertiary' onClick={() => setDialogOpen(false)}>
                                    {t('controls.cancel', 'Cancel')}
                                </Button>
                                <Button onClick={save}>{t('controls.save', 'Save')}</Button>
                            </Stack>
                        </DialogActions>
                    </ResponsiveDialog>
                </>
            )}
        </SettingsSection>
    );
};

const Requirement = styled(Text)(({ $satisfied, theme }) => ({
    color: $satisfied ? theme.palette.ensightful.success[700] : theme.palette.ensightful.neutral[600],
}));

export default ChangePassword;
